import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { UserDetails } from 'src/shared/models/common';
import { UserService } from 'src/shared/services/user.service';

/**
 * Resolves the team member for the detail screen.
 *
 * Fast path: when navigated from the team grid, the member is passed via
 * navigation state — use it as-is (no extra API call).
 *
 * Refresh / deep-link: navigation state is gone, so rebuild the member from the
 * `:id` (uniqueId) route param. Running this as a resolver guarantees the shell
 * and child tabs activate with the member already available, instead of
 * bouncing back to /app/my-team.
 */
export const viewDetailMemberResolver: ResolveFn<UserDetails | null> = async (
  route,
) => {
  const router = inject(Router);
  const userService = inject(UserService);

  const stateUser = router.getCurrentNavigation()?.extras?.state?.[
    'user'
  ] as UserDetails | undefined;
  if (stateUser) return stateUser;

  const uniqueId = route.paramMap.get('id');
  if (!uniqueId) return null;

  try {
    const user = await userService.getUserByUniqueId(uniqueId);
    if (!user) return null;
    return {
      ...user,
      // getUserByUniqueId doesn't return a composed userName; rebuild it so the
      // header label + initials render the same as the grid navigation path.
      userName:
        user.userName ||
        `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    } as UserDetails;
  } catch {
    return null;
  }
};
